package app

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.attributes.AttrsScope
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.accept
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.attributes.ref
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Canvas
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.H4
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLButtonElement
import org.w3c.dom.HTMLCanvasElement
import org.w3c.dom.HTMLImageElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.HTMLLabelElement
import org.w3c.dom.events.Event
import org.w3c.dom.events.KeyboardEvent
import org.w3c.files.Blob
import org.w3c.files.File
import org.w3c.files.FileReader
import kotlin.js.Date
import kotlin.math.abs
import kotlin.math.sqrt

@Composable
fun App() {
    var tool by remember { mutableStateOf(Tool.Rectangle) }
    var blurIntensity by remember { mutableStateOf(10) }
    var areas by remember { mutableStateOf(emptyList<BlurArea>()) }
    val history = remember { HistoryState(emptyList<BlurArea>()) }
    var image by remember { mutableStateOf<HTMLImageElement?>(null) }
    var canvasElement by remember { mutableStateOf<HTMLCanvasElement?>(null) }
    var isDrawing by remember { mutableStateOf(false) }
    var isPanning by remember { mutableStateOf(false) }
    var lastPan by remember { mutableStateOf(Point(0.0, 0.0)) }
    var startPoint by remember { mutableStateOf(Point(0.0, 0.0)) }
    var currentArea by remember { mutableStateOf<BlurArea?>(null) }
    var currentPath by remember { mutableStateOf(emptyList<Point>()) }
    var view by remember { mutableStateOf(ViewState()) }
    var showOverlay by remember { mutableStateOf(true) }
    var status by remember { mutableStateOf("Load image to begin") }
    var cachedExport by remember { mutableStateOf<CachedExport?>(null) }
    val exportPreparation = remember { ExportPreparation() }

    fun redraw() {
        val canvas = canvasElement ?: return
        BlurCanvas(canvas).draw(image, areas, currentArea, currentPath, tool, showOverlay, view)
    }

    fun persist(nextAreas: List<BlurArea> = areas, nextIntensity: Int = blurIntensity) {
        saveStoredState(nextAreas, nextIntensity)
    }

    fun loadFile(file: File) {
        if (!file.type.startsWith("image/")) {
            status = "Please choose an image file"
            return
        }
        status = "Loading image..."
        val reader = FileReader()
        reader.onload = { _: Event ->
            val loaded = document.createElement("img") as HTMLImageElement
            loaded.onload = { _: Event ->
                image = loaded
                view = ViewState()
                areas = history.reset(emptyList())
                persist(emptyList())
                status = "Image loaded"
            }
            loaded.onerror = { _: dynamic, _: String, _: Int, _: Int, _: dynamic ->
                status = "Could not decode image"
            }
            loaded.src = reader.result as String
        }
        reader.onerror = { _: Event ->
            status = "Could not read file"
        }
        reader.readAsDataURL(file)
    }

    fun pointFromPointer(clientX: Double, clientY: Double): Point {
        val canvas = canvasElement ?: return Point(0.0, 0.0)
        val currentImage = image ?: return Point(0.0, 0.0)
        val rect = canvas.getBoundingClientRect()
        val fit = fitImage(currentImage.naturalWidth.toDouble(), currentImage.naturalHeight.toDouble(), CanvasWidth, CanvasHeight)
        return toImagePoint(clientX, clientY, rect.left, rect.top, fit, view, canvas.width / rect.width, canvas.height / rect.height)
    }

    fun canvasPointFromPointer(clientX: Double, clientY: Double): Point {
        val canvas = canvasElement ?: return Point(0.0, 0.0)
        val rect = canvas.getBoundingClientRect()
        return toCanvasPoint(clientX, clientY, rect.left, rect.top, canvas.width / rect.width, canvas.height / rect.height)
    }

    fun panDeltaFromPointer(clientX: Double, clientY: Double): Point {
        val canvas = canvasElement ?: return Point(0.0, 0.0)
        val rect = canvas.getBoundingClientRect()
        return Point(
            x = (clientX - lastPan.x) * canvas.width / rect.width,
            y = (clientY - lastPan.y) * canvas.height / rect.height
        )
    }

    fun undo() {
        areas = history.undo()
        persist(areas)
    }

    fun redo() {
        areas = history.redo()
        persist(areas)
    }

    fun clearAll() {
        areas = history.push(emptyList())
        persist(areas)
        status = "Cleared all areas"
    }

    fun prepareExport(currentImage: HTMLImageElement, currentAreas: List<BlurArea>) {
        if (currentAreas.isEmpty()) return
        if (exportPreparation.image === currentImage && exportPreparation.areas == currentAreas) return

        cachedExport = null
        val revision = ++exportPreparation.revision
        exportPreparation.image = currentImage
        exportPreparation.areas = currentAreas
        exportBlurredImage(currentImage, currentAreas) { blob ->
            if (revision != exportPreparation.revision) return@exportBlurredImage
            exportPreparation.image = null
            exportPreparation.areas = null
            if (blob == null) {
                if (status == PreparingExportStatus) status = "Export failed"
            } else {
                cachedExport = CachedExport(currentImage, currentAreas, blob)
                if (status == PreparingExportStatus) status = "PNG ready; tap Share again"
            }
        }
    }

    fun deliverExport() {
        val currentImage = image ?: return
        if (areas.isEmpty()) return
        val fileName = "blurred-${Date.now().toLong()}.png"
        val blob = cachedExport?.takeIf { it.image === currentImage && it.areas == areas }?.blob
        if (blob == null) {
            prepareExport(currentImage, areas)
            status = PreparingExportStatus
            return
        }
        shareOrDownload(blob, fileName, onStatus = { status = it })
    }

    LaunchedEffect(Unit) {
        val stored = loadStoredState()
        areas = stored.areas
        blurIntensity = stored.blurIntensity
        history.reset(stored.areas)
    }

    DisposableEffect(image, areas, currentArea, currentPath, tool, showOverlay, view, canvasElement) {
        redraw()
        onDispose { }
    }

    DisposableEffect(image, areas) {
        cachedExport = null
        exportPreparation.revision++
        exportPreparation.image = null
        exportPreparation.areas = null
        val currentImage = image
        if (currentImage != null && areas.isNotEmpty()) prepareExport(currentImage, areas)
        onDispose { }
    }

    DisposableEffect(Unit) {
        val listener: (Event) -> Unit = { event ->
            val key = event as KeyboardEvent
            if (key.ctrlKey || key.metaKey) {
                when (key.key.lowercase()) {
                    "z" -> {
                        key.preventDefault()
                        if (key.shiftKey) redo() else undo()
                    }
                    "s" -> {
                        key.preventDefault()
                        deliverExport()
                    }
                    "a" -> {
                        key.preventDefault()
                        clearAll()
                    }
                }
            } else {
                when (key.key) {
                    "1" -> tool = Tool.Rectangle
                    "2" -> tool = Tool.Circle
                    "3" -> tool = Tool.Freehand
                    " " -> {
                        key.preventDefault()
                        tool = if (tool == Tool.Pan) Tool.Rectangle else Tool.Pan
                    }
                    "h", "H" -> showOverlay = !showOverlay
                }
            }
        }
        window.addEventListener("keydown", listener)
        onDispose { window.removeEventListener("keydown", listener) }
    }

    Div({ classNames("page") }) {
        Header(status)
        Div({ classNames("workspace") }) {
            Toolbar(
                tool = tool,
                onToolChange = { tool = it },
                blurIntensity = blurIntensity,
                onBlurIntensity = {
                    blurIntensity = it
                    persist(nextIntensity = it)
                },
                view = view,
                showOverlay = showOverlay,
                onShowOverlay = { showOverlay = it },
                canUndo = history.canUndo,
                canRedo = history.canRedo,
                areaCount = areas.size,
                historyLabel = history.positionLabel(),
                onUndo = ::undo,
                onRedo = ::redo,
                onClear = ::clearAll,
                onShare = ::deliverExport,
                hasImage = image != null
            )
            CanvasPanel(
                imageLoaded = image != null,
                isDrawing = isDrawing,
                tool = tool,
                areaCount = areas.size,
                onFile = ::loadFile,
                canvasRef = { canvas ->
                    canvasElement = canvas
                    BlurCanvas(canvas).configure()
                },
                onPointerDown = { clientX, clientY ->
                    if (image == null) return@CanvasPanel
                    if (tool == Tool.Pan) {
                        isPanning = true
                        lastPan = Point(clientX, clientY)
                    } else {
                        val point = pointFromPointer(clientX, clientY)
                        isDrawing = true
                        startPoint = point
                        if (tool == Tool.Freehand) {
                            currentPath = listOf(point)
                        } else {
                            currentArea = BlurArea(
                                id = "b-${Date.now().toLong()}",
                                x = point.x,
                                y = point.y,
                                width = 0.0,
                                height = 0.0,
                                shape = if (tool == Tool.Circle) BlurShape.Circle else BlurShape.Rectangle,
                                blurRadius = blurIntensity
                            )
                        }
                    }
                },
                onPointerMove = { clientX, clientY ->
                    if (isPanning) {
                        val delta = panDeltaFromPointer(clientX, clientY)
                        view = view.copy(offsetX = view.offsetX + delta.x, offsetY = view.offsetY + delta.y)
                        lastPan = Point(clientX, clientY)
                    } else if (isDrawing) {
                        val point = pointFromPointer(clientX, clientY)
                        if (tool == Tool.Freehand) {
                            currentPath = currentPath + point
                        } else {
                            currentArea = currentArea?.copy(
                                x = minOf(startPoint.x, point.x),
                                y = minOf(startPoint.y, point.y),
                                width = abs(point.x - startPoint.x),
                                height = abs(point.y - startPoint.y)
                            )
                        }
                    }
                },
                onPointerEnd = {
                    if (isPanning) {
                        isPanning = false
                        return@CanvasPanel
                    }
                    if (isDrawing) {
                        val nextArea = if (tool == Tool.Freehand && currentPath.size > 2) {
                            boundsOf(currentPath).copy(id = "b-${Date.now().toLong()}", blurRadius = blurIntensity)
                        } else {
                            currentArea
                        }
                        if (nextArea != null && nextArea.width > 5.0 && nextArea.height > 5.0) {
                            areas = history.push(areas + nextArea)
                            persist(areas)
                            status = "Areas blurred: ${areas.size}"
                        }
                    }
                    isDrawing = false
                    currentArea = null
                    currentPath = emptyList()
                },
                onPointerCancel = {
                    isPanning = false
                    isDrawing = false
                    currentArea = null
                    currentPath = emptyList()
                },
                onPinchStart = {
                    isPanning = false
                    isDrawing = false
                    currentArea = null
                    currentPath = emptyList()
                },
                onPinchMove = { centerX, centerY, scale ->
                    val center = canvasPointFromPointer(centerX, centerY)
                    view = zoomAroundCanvasPoint(view, center, view.zoom * scale)
                }
            )
        }
    }
}

@Composable
private fun Header(status: String) {
    Div({ classNames("header") }) {
        H1 { Text("Image Fragment Blur Service") }
        P { Text("Select areas of an image and export a blurred PNG") }
        Div({ classNames("shortcut-row") }) {
            Badge("Ctrl+Z", "Undo")
            Badge("Ctrl+S", "Share")
            Badge("1 2 3", "Tools")
            Badge("Space", "Pan")
            Badge("H", "Overlay")
        }
        P({ classNames("status") }) { Text(status) }
    }
}

@Composable
private fun Toolbar(
    tool: Tool,
    onToolChange: (Tool) -> Unit,
    blurIntensity: Int,
    onBlurIntensity: (Int) -> Unit,
    view: ViewState,
    showOverlay: Boolean,
    onShowOverlay: (Boolean) -> Unit,
    canUndo: Boolean,
    canRedo: Boolean,
    areaCount: Int,
    historyLabel: String,
    onUndo: () -> Unit,
    onRedo: () -> Unit,
    onClear: () -> Unit,
    onShare: () -> Unit,
    hasImage: Boolean
) {
    Div({ classNames("panel", "toolbar") }) {
        H3 { Text("Tools") }
        ToolButton("Rectangle", "1", Tool.Rectangle, tool, onToolChange)
        ToolButton("Circle", "2", Tool.Circle, tool, onToolChange)
        ToolButton("Freehand", "3", Tool.Freehand, tool, onToolChange)
        ToolButton("Pan", "Space", Tool.Pan, tool, onToolChange)

        Div({ classNames("section", "view-section") }) {
            H4 { Text("View") }
            P({ classNames("muted") }) { Text("Scale: ${view.zoom.times(100).toInt()}%") }
            Button({ classNames("secondary-button", "full-button"); click { onShowOverlay(!showOverlay) } }) {
                Text(if (showOverlay) "Hide area boundaries" else "Show area boundaries")
            }
        }

        Div({ classNames("section") }) {
            Label {
                Text("Blur intensity: ${blurIntensity}px")
                Input(InputType.Range) {
                    classNames("slider")
                    attr("min", "1")
                    attr("max", "20")
                    attr("step", "1")
                    attr("value", blurIntensity.toString())
                    ref { element ->
                        element.oninput = {
                            onBlurIntensity(element.value.toIntOrNull() ?: blurIntensity)
                            null
                        }
                        onDispose { element.oninput = null }
                    }
                }
            }
        }

        Div({ classNames("section", "actions") }) {
            Button({ classNames("secondary-button"); if (!canUndo) disabled(); click { onUndo() } }) { Text("Undo") }
            Button({ classNames("secondary-button"); if (!canRedo) disabled(); click { onRedo() } }) { Text("Redo") }
            Button({ classNames("danger-button"); if (areaCount == 0) disabled(); click { onClear() } }) { Text("Clear all") }
            Button({ classNames("primary-button"); if (!hasImage || areaCount == 0) disabled(); click { onShare() } }) { Text("Share result") }
        }

        Div({ classNames("stats") }) {
            Stat("Areas", areaCount.toString())
            Stat("Tool", tool.label)
            Stat("History", historyLabel)
        }
    }
}

@Composable
private fun ToolButton(label: String, key: String, value: Tool, current: Tool, onToolChange: (Tool) -> Unit) {
    Button({
        classNames(if (value == current) "tool-button-active" else "tool-button")
        click { onToolChange(value) }
    }) {
        Span { Text(label) }
        Span({ classNames("key") }) { Text(key) }
    }
}

@Composable
private fun CanvasPanel(
    imageLoaded: Boolean,
    isDrawing: Boolean,
    tool: Tool,
    areaCount: Int,
    onFile: (File) -> Unit,
    canvasRef: (HTMLCanvasElement) -> Unit,
    onPointerDown: (Double, Double) -> Unit,
    onPointerMove: (Double, Double) -> Unit,
    onPointerEnd: () -> Unit,
    onPointerCancel: () -> Unit,
    onPinchStart: () -> Unit,
    onPinchMove: (Double, Double, Double) -> Unit
) {
    Div({ classNames("panel", "canvas-panel") }) {
        if (!imageLoaded) {
            Label(attrs = {
                classNames("drop-zone")
                ref { element ->
                    element.ondragover = { event ->
                        event.preventDefault()
                        null
                    }
                    element.ondrop = { rawEvent ->
                        rawEvent.preventDefault()
                        val files = rawEvent.asDynamic().dataTransfer?.files
                        val length = (files?.length as? Int) ?: 0
                        var first: File? = null
                        for (index in 0 until length) {
                            val file = files.item(index).unsafeCast<File>()
                            if (file.type.startsWith("image/")) {
                                first = file
                                break
                            }
                        }
                        if (first != null) onFile(first)
                        null
                    }
                    onDispose {
                        element.ondragover = null
                        element.ondrop = null
                    }
                }
            }) {
                Div({ classNames("upload-icon") }) { Text("Upload") }
                H3 { Text("Load image") }
                P { Text("Drag and drop or click to choose") }
                Div({ classNames("format-row") }) {
                    listOf("JPG", "PNG", "GIF", "WebP").forEach { Span({ classNames("format") }) { Text(it) } }
                }
                Input(InputType.File) {
                    classNames("hidden-input")
                    accept("image/*")
                    ref { element ->
                        element.onchange = {
                            val file = element.files?.item(0)
                            if (file != null) onFile(file)
                            null
                        }
                        onDispose { element.onchange = null }
                    }
                }
            }
        } else {
            Div({ classNames("canvas-frame") }) {
                Canvas({
                    classNames("canvas", if (tool == Tool.Pan) "cursor-pan" else "cursor-draw")
                    ref { element ->
                        canvasRef(element)
                        var activePointerId: Int? = null
                        val pointers = mutableMapOf<Int, Point>()
                        var pinchLastDistance: Double? = null

                        fun pointerPoint(pointer: dynamic): Point {
                            return Point(browserNumber(pointer.clientX), browserNumber(pointer.clientY))
                        }

                        fun pinchPoints(): List<Point> = pointers.values.take(2)

                        fun distance(points: List<Point>): Double {
                            val dx = points[0].x - points[1].x
                            val dy = points[0].y - points[1].y
                            return sqrt(dx * dx + dy * dy)
                        }

                        fun center(points: List<Point>): Point {
                            return Point(
                                x = (points[0].x + points[1].x) / 2.0,
                                y = (points[0].y + points[1].y) / 2.0
                            )
                        }

                        fun releasePointer(pointerId: Int) {
                            if (element.hasPointerCapture(pointerId)) element.releasePointerCapture(pointerId)
                        }

                        fun cancelActiveGesture() {
                            activePointerId = null
                            onPointerCancel()
                        }

                        fun clearPointer(pointerId: Int) {
                            val wasPinching = pinchLastDistance != null
                            pointers.remove(pointerId)
                            if (wasPinching) {
                                pinchLastDistance = null
                                activePointerId = null
                            }
                        }

                        element.onpointerdown = { event ->
                            val pointer = event.asDynamic()
                            pointer.preventDefault()
                            val pointerId = pointer.pointerId.unsafeCast<Int>()
                            if (pointers.size < 2 && !pointers.containsKey(pointerId)) {
                                pointers[pointerId] = pointerPoint(pointer)
                                element.setPointerCapture(pointerId)
                            }
                            if (pointers.size == 1 && activePointerId == null && pinchLastDistance == null) {
                                activePointerId = pointerId
                                val point = pointers.getValue(pointerId)
                                onPointerDown(point.x, point.y)
                            } else if (pointers.size == 2) {
                                cancelActiveGesture()
                                val points = pinchPoints()
                                pinchLastDistance = distance(points)
                                onPinchStart()
                            }
                            null
                        }
                        element.onpointermove = { event ->
                            val pointer = event.asDynamic()
                            pointer.preventDefault()
                            val pointerId = pointer.pointerId.unsafeCast<Int>()
                            if (pointers.containsKey(pointerId)) {
                                pointers[pointerId] = pointerPoint(pointer)
                            }
                            val lastDistance = pinchLastDistance
                            if (lastDistance != null && pointers.size == 2) {
                                val points = pinchPoints()
                                val nextDistance = distance(points)
                                if (lastDistance > 0.0 && nextDistance > 0.0) {
                                    val center = center(points)
                                    onPinchMove(center.x, center.y, nextDistance / lastDistance)
                                    pinchLastDistance = nextDistance
                                }
                            } else if (activePointerId == pointerId) {
                                val point = pointers[pointerId] ?: pointerPoint(pointer)
                                onPointerMove(point.x, point.y)
                            }
                            null
                        }
                        element.onpointerup = { event ->
                            val pointer = event.asDynamic()
                            pointer.preventDefault()
                            val pointerId = pointer.pointerId.unsafeCast<Int>()
                            if (activePointerId == pointerId) {
                                activePointerId = null
                                clearPointer(pointerId)
                                releasePointer(pointerId)
                                onPointerEnd()
                            } else if (pointers.containsKey(pointerId)) {
                                clearPointer(pointerId)
                                releasePointer(pointerId)
                            }
                            null
                        }
                        element.onpointercancel = { event ->
                            val pointer = event.asDynamic()
                            pointer.preventDefault()
                            val pointerId = pointer.pointerId.unsafeCast<Int>()
                            if (activePointerId == pointerId) {
                                cancelActiveGesture()
                            }
                            if (pointers.containsKey(pointerId)) {
                                clearPointer(pointerId)
                                releasePointer(pointerId)
                            }
                            null
                        }
                        element.onlostpointercapture = { event ->
                            val pointerId = event.asDynamic().pointerId.unsafeCast<Int>()
                            if (activePointerId == pointerId) {
                                cancelActiveGesture()
                            }
                            if (pointers.containsKey(pointerId)) {
                                clearPointer(pointerId)
                            }
                            null
                        }
                        onDispose {
                            pointers.keys.toList().forEach { releasePointer(it) }
                            pointers.clear()
                            pinchLastDistance = null
                            activePointerId = null
                            element.onpointerdown = null
                            element.onpointermove = null
                            element.onpointerup = null
                            element.onpointercancel = null
                            element.onlostpointercapture = null
                        }
                    }
                })
            }
            Div({ classNames("canvas-status") }) {
                when {
                    isDrawing && tool == Tool.Freehand -> Span({ classNames("badge", "badge-active") }) { Text("Drawing freehand area...") }
                    isDrawing -> Span({ classNames("badge", "badge-active") }) { Text("Selecting area...") }
                    areaCount == 0 -> Span({ classNames("badge") }) { Text("Choose a tool and create a blur area") }
                    else -> Span({ classNames("badge", "badge-active") }) { Text("Areas blurred: $areaCount") }
                }
            }
        }
    }
}

@Composable
private fun Badge(key: String, value: String) {
    Span({ classNames("badge") }) {
        Text("$key - $value")
    }
}

@Composable
private fun Stat(label: String, value: String) {
    Div({ classNames("stat") }) {
        Span { Text(label) }
        Span({ classNames("badge") }) { Text(value) }
    }
}

private val Tool.label: String
    get() = when (this) {
        Tool.Rectangle -> "Rectangle"
        Tool.Circle -> "Circle"
        Tool.Freehand -> "Freehand"
        Tool.Pan -> "Pan"
    }

private fun AttrsScope<*>.classNames(vararg names: String) {
    attr("class", names.filter { it.isNotBlank() }.joinToString(" "))
}

private fun AttrsScope<HTMLButtonElement>.click(handler: () -> Unit) {
    onClick { event ->
        event.preventDefault()
        handler()
    }
}

private const val PreparingExportStatus = "Preparing PNG; tap Share again"

private data class CachedExport(val image: HTMLImageElement, val areas: List<BlurArea>, val blob: Blob)

private class ExportPreparation {
    var revision = 0
    var image: HTMLImageElement? = null
    var areas: List<BlurArea>? = null
}

private fun shareOrDownload(blob: Blob, fileName: String, onStatus: (String) -> Unit) {
    val navigator = window.navigator.asDynamic()
    val file = js("new File([blob], fileName, { type: 'image/png' })").unsafeCast<File>()
    val shareData = js("({})")
    shareData.files = arrayOf(file)

    if (navigator.canShare == undefined || navigator.share == undefined || !navigator.canShare(shareData).unsafeCast<Boolean>()) {
        downloadPng(blob, fileName)
        onStatus("Downloaded PNG")
        return
    }

    navigator.share(shareData).then({
        onStatus("Shared PNG")
        null
    }).catch({ error: dynamic ->
        if (error?.name != "AbortError") {
            downloadPng(blob, fileName)
            onStatus("Sharing failed; downloaded PNG")
        }
        null
    })
}
